/* BattleInstructions — first-time tutorial overlay for the Battle Arena.
 *
 * Shown ONCE per user (persisted in localStorage as `wq_battle_intro_seen`)
 * on the very first battle. Auto-dismisses after ~4.5s or on any tap.
 * Includes a subtle animated tap gesture so the mechanic is unmistakable.
 *
 * Pass `force={true}` to re-show it (useful for a Settings "replay tutorial"
 * item in a future iteration).
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Sparkles, Flame } from 'lucide-react';

const SEEN_KEY = 'wq_battle_intro_seen';

export default function BattleInstructions({ force = false }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (force) { setVisible(true); return; }
      const seen = localStorage.getItem(SEEN_KEY);
      if (!seen) setVisible(true);
    } catch (_e) { setVisible(true); }
  }, [force]);

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => dismiss(), 5200);
    return () => clearTimeout(t);
  }, [visible]);

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch (_e) { /* ignore */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28 }}
          role="button"
          tabIndex={0}
          aria-label="Dismiss battle briefing"
          onClick={dismiss}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); dismiss(); } }}
          data-testid="battle-instructions-overlay"
          className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center px-5 pb-8 pt-6 bg-black/55 backdrop-blur-sm cursor-pointer"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{ delay: 0.05, duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
            className="w-full max-w-sm rounded-3xl border border-emerald-400/45 bg-gradient-to-br from-emerald-500/15 via-black/85 to-black/95 backdrop-blur-xl p-5 space-y-4 shadow-[0_0_50px_-8px_rgba(74,222,128,0.55)]"
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <p className="text-[10px] uppercase tracking-[0.35em] text-emerald-200">Battle briefing</p>
            </div>

            <h2 className="font-serif text-2xl leading-tight">
              Tap as fast as you can<br/>
              <span className="text-emerald-300">to strike your opponent.</span>
            </h2>

            {/* Animated tap gesture */}
            <div className="relative h-24 rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-500/15 to-black/60 overflow-hidden">
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                animate={{ scale: [1, 0.92, 1] }}
                transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div className="flex flex-col items-center gap-1">
                  <Zap className="w-8 h-8 text-emerald-300" strokeWidth={2} />
                  <span className="text-[10px] uppercase tracking-widest text-emerald-100">Tap · Tap · Tap</span>
                </div>
              </motion.div>
              {/* Ripple */}
              <motion.span
                className="absolute left-1/2 top-1/2 w-4 h-4 -ml-2 -mt-2 rounded-full border-2 border-emerald-300/70"
                animate={{ scale: [1, 3.8], opacity: [0.85, 0] }}
                transition={{ duration: 1.1, repeat: Infinity, ease: 'easeOut' }}
              />
            </div>

            <ul className="space-y-2 text-[13px] text-white/90">
              <li className="flex items-start gap-2" data-testid="battle-tip-tap">
                <Zap className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" />
                <span><b className="text-emerald-200">Faster taps = harder strikes.</b> Every tap consumes energy — the faster you spam, the more damage you land in a round.</span>
              </li>
              <li className="flex items-start gap-2" data-testid="battle-tip-ultimate">
                <Sparkles className="w-4 h-4 mt-0.5 text-amber-300 shrink-0" />
                <span><b className="text-amber-200">Charge the Ultimate.</b> Tap enough and the ⚡ meter fills — release it for a signature move.</span>
              </li>
              <li className="flex items-start gap-2" data-testid="battle-tip-combo">
                <Flame className="w-4 h-4 mt-0.5 text-fuchsia-300 shrink-0" />
                <span><b className="text-fuchsia-200">Chain combos.</b> Keep the pressure on — 5+ hits in a row unlock devastating combo bonuses.</span>
              </li>
              <li className="flex items-start gap-2" data-testid="battle-tip-ecology">
                <span className="w-4 h-4 mt-0.5 text-teal-300 shrink-0">🌿</span>
                <span><b className="text-teal-200">Ecology matters.</b> Habitat, weather, time-of-day and season all shift stats: wolves surge at night, amphibians dominate in rain, ravens hunt best in cold.</span>
              </li>
              <li className="flex items-start gap-2" data-testid="battle-tip-passives">
                <span className="w-4 h-4 mt-0.5 text-indigo-300 shrink-0">✦</span>
                <span><b className="text-indigo-200">Passive abilities</b> are drawn from real biology — Pack Hunter, Night Hunter, Ecosystem Engineer. Tap ⓘ on a card to see which passives fire in the current biome.</span>
              </li>
            </ul>

            <div className="pt-1 flex items-center justify-between text-[10px] uppercase tracking-widest text-white/50">
              <span>Tap anywhere to dismiss</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); dismiss(); }}
                data-testid="battle-instructions-got-it"
                className="px-3 py-1.5 rounded-full bg-emerald-400 text-black text-[11px] tracking-widest"
              >
                Got it
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
